import type { ApprovalHistoryEvent, ApprovalRequestResponse } from '@schemas/api';
import { isTenantAdminUser, type User } from '@store/authStore';

interface DocumentPermissionOptions {
  currentUser: User | null | undefined;
  hasPermission: (permission: string) => boolean;
  approvalRequest?: ApprovalRequestResponse | null;
  approvalHistory?: ApprovalHistoryEvent[];
}

export interface DocumentPermissionState {
  hasApprovalChain: boolean;
  isApprovalPending: boolean;
  isTenantAdmin: boolean;
  isAssignedToAnyApprovalStep: boolean;
  isAssignedToCurrentApprovalStep: boolean;
  canViewDocument: boolean;
  canApprove: boolean;
  canReject: boolean;
  canDelete: boolean;
  canSubmitCorrections: boolean;
  viewOnlyReason: string | null;
  approveLockReason: string | null;
  rejectLockReason: string | null;
  deleteLockReason: string | null;
  correctionsLockReason: string | null;
}

const getApproverIds = (
  approvalHistory: ApprovalHistoryEvent[],
  stepNumber?: number
): Set<string> => {
  const decisionEvents = approvalHistory.filter((event) => event.event_type === 'decision');

  return new Set(
    decisionEvents
      .filter((event) => stepNumber === undefined || event.step_number === stepNumber)
      .map((event) => event.approver_id)
      .filter((approverId): approverId is string => Boolean(approverId))
  );
};

const getApprovalAssignmentLockReason = ({
  hasApprovalChain,
  isApprovalPending,
  isAssignedToAnyApprovalStep,
  isAssignedToCurrentApprovalStep,
}: Pick<
  DocumentPermissionState,
  'hasApprovalChain' | 'isApprovalPending' | 'isAssignedToAnyApprovalStep' | 'isAssignedToCurrentApprovalStep'
>): string | null => {
  if (!hasApprovalChain) {
    return null;
  }

  if (!isAssignedToAnyApprovalStep) {
    return 'Only users assigned to this approval chain can act on this document.';
  }

  if (isApprovalPending && !isAssignedToCurrentApprovalStep) {
    return 'This approval chain is waiting on another step. Actions unlock when your step becomes active.';
  }

  return null;
};

export const getDocumentPermissionState = ({
  currentUser,
  hasPermission,
  approvalRequest,
  approvalHistory = [],
}: DocumentPermissionOptions): DocumentPermissionState => {
  const isTenantAdmin = isTenantAdminUser(currentUser);
  const hasApprovalChain = Boolean(approvalRequest?.id);
  const isApprovalPending = approvalRequest?.status === 'pending';
  const currentUserId = currentUser?.id ?? null;
  const hasDocumentReadPermission = isTenantAdmin || hasPermission('document.read');
  const hasDocumentApprovePermission = isTenantAdmin || hasPermission('document.approve');
  const hasDocumentDiscardPermission = isTenantAdmin || hasPermission('document.discard');
  const anyStepApprovers = getApproverIds(approvalHistory);
  const currentStepApprovers = approvalRequest?.current_step
    ? getApproverIds(approvalHistory, approvalRequest.current_step)
    : new Set<string>();

  const isAssignedToAnyApprovalStep = Boolean(
    isTenantAdmin || (currentUserId && anyStepApprovers.has(currentUserId))
  );
  const isAssignedToCurrentApprovalStep = Boolean(
    isTenantAdmin || (currentUserId && currentStepApprovers.has(currentUserId))
  );

  const assignmentLockReason = getApprovalAssignmentLockReason({
    hasApprovalChain,
    isApprovalPending,
    isAssignedToAnyApprovalStep,
    isAssignedToCurrentApprovalStep,
  });

  const canActOnDocument = hasApprovalChain
    ? assignmentLockReason === null
    : hasDocumentApprovePermission || hasDocumentDiscardPermission;

  const viewOnlyReason =
    assignmentLockReason ??
    (hasDocumentReadPermission && !canActOnDocument
      ? 'You can view this document, but your role does not include document approval or discard permissions.'
      : null);

  const approveLockReason =
    assignmentLockReason ??
    (hasDocumentApprovePermission ? null : 'Requires document.approve permission.');
  const rejectLockReason =
    assignmentLockReason ??
    (hasDocumentDiscardPermission ? null : 'Requires document.discard permission.');
  const deleteLockReason =
    assignmentLockReason ??
    (hasDocumentDiscardPermission ? null : 'Requires document.discard permission.');
  const correctionsLockReason =
    assignmentLockReason ??
    (hasDocumentApprovePermission ? null : 'Requires document.approve permission.');

  return {
    hasApprovalChain,
    isApprovalPending,
    isTenantAdmin,
    isAssignedToAnyApprovalStep,
    isAssignedToCurrentApprovalStep,
    canViewDocument: hasApprovalChain ? isAssignedToAnyApprovalStep : hasDocumentReadPermission,
    canApprove: approveLockReason === null,
    canReject: rejectLockReason === null,
    canDelete: deleteLockReason === null,
    canSubmitCorrections: correctionsLockReason === null,
    viewOnlyReason,
    approveLockReason,
    rejectLockReason,
    deleteLockReason,
    correctionsLockReason,
  };
};
